import { useContext } from 'react';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { SheetMenu, type SheetMenuItem } from '../primitives/SheetMenu';
import { useTheme } from './theme';

// Bottom action-sheet for managing an EXISTING superset (inbox 963ca6f2, batch
// 2). Reachable from the ☰ on the group header in BOTH loggers. One discoverable
// home for: save-as-template, add a member, reorder (up/down on the shown
// member), remove a member, dissolve. (Rename removed AP-0n — the superset has
// no visible name any more, so there was nothing to rename.)
export function SupersetMenuSheet({
  visible,
  groupLabel,
  canMoveUp,
  canMoveDown,
  onSaveTemplate,
  onAddExercise,
  onMoveUp,
  onMoveDown,
  onRemoveMember,
  onSplit,
  onClose,
}: {
  visible: boolean;
  groupLabel: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** Absent ⇒ the row is not rendered at all (PE-26). ⛔ Never disabled-but-
   *  present: a control that announces itself and does nothing is the defect
   *  this optionality exists to prevent. */
  onSaveTemplate?: () => void;
  onAddExercise: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemoveMember: () => void;
  onSplit: () => void;
  onClose: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  // The context, not the hook: no provider (a bare test mount) means no inset.
  const insets = useContext(SafeAreaInsetsContext);

  const items: SheetMenuItem[] = [
    ...(onSaveTemplate
      ? [{ key: 'template', icon: 'star' as const, fill: true, label: t('mobile.superset.menuSaveTemplate', { defaultValue: 'Save as template' }), onPress: onSaveTemplate }]
      : []),
    { key: 'add', icon: 'plus', label: t('mobile.superset.menuAddExercise', { defaultValue: 'Add exercise' }), onPress: onAddExercise },
    { key: 'up', icon: 'chevron-up', label: t('mobile.superset.menuMoveUp', { defaultValue: 'Move up' }), onPress: onMoveUp, disabled: !canMoveUp },
    { key: 'down', icon: 'chevron-down', label: t('mobile.superset.menuMoveDown', { defaultValue: 'Move down' }), onPress: onMoveDown, disabled: !canMoveDown },
    { key: 'remove', icon: 'trash', label: t('mobile.superset.menuRemoveMember', { defaultValue: 'Remove this exercise' }), onPress: onRemoveMember },
    { key: 'split', icon: 'scissors', label: t('mobile.superset.menuSplit', { defaultValue: 'Dissolve superset' }), onPress: onSplit, destructive: true },
  ];

  return (
    <SheetMenu
      theme={theme}
      visible={visible}
      onClose={onClose}
      title={groupLabel}
      cancelLabel={t('mobile.logger.cancel', { defaultValue: 'Cancel' })}
      items={items}
      bottomInset={insets?.bottom ?? 0}
    />
  );
}
